import AuthenticatedLayout from "@/components/AuthenticatedLayout";
import BackToDashboard from "@/components/BackToDashboard";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDateFromISO } from "@/lib/formatters";
import { Subscription } from "@/types";
import { PageProps } from "@/types/PageProps";
import { Head } from "@inertiajs/react";
import { cloneElement, ReactElement, ReactNode } from "react";

type Props = PageProps & {
  back?: {
    url: string;
    label: string;
  };
  stripe_customer_id: string | null;
  subscription: Subscription | null;
};

function BillingCardShell({ children }: { children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">Billing</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function SignUpFormWrapper({ children }: { children: ReactElement }) {
  const authenticityToken = document
    .querySelector('meta[name="csrf-token"]')
    ?.getAttribute("content");

  const childWithSubmitType = cloneElement(children, { type: "submit" });

  return (
    <form action="/billing/subscribe" method="POST">
      {authenticityToken && (
        <input type="hidden" name="authenticity_token" value={authenticityToken} />
      )}
      <input type="hidden" name="lookup_key" value="standard_monthly" />
      {childWithSubmitType}
    </form>
  );
}

function PlanDetails({ buttonText }: { buttonText: string }) {
  return (
    <Card>
      <CardHeader>Teeba</CardHeader>
      <CardContent>
        <div className="space-x-2">
          <span className="font-semibold text-5xl">$20</span>
          <span className="text-muted-foreground ">/month</span>
        </div>

        <div className="py-6">See how Teeba can help with your market based adjustments.</div>

        <div>
          <ul className="list-disc ml-4 space-y-2">
            <li>Market conditions adjustments in compliance with new GSE requirements</li>
            <li>Generate unlimited reports each month</li>
            <li>Storage for all completed reports</li>
            <li>Customer support and troubleshooting</li>
          </ul>
        </div>

        <div className="pt-8">
          <SignUpFormWrapper>
            <Button size="lg" className="w-full">
              {buttonText}
            </Button>
          </SignUpFormWrapper>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Component({ user, errors, notice, subscription }: Props) {
  // sign up form, for new users
  if (!subscription) {
    return (
      <AuthenticatedLayout user={user} errors={errors} notice={notice}>
        <Head title="Billing" />
        <div className="max-w-md mx-auto pt-8">
          <PlanDetails buttonText="Start a free 7 day trial" />
        </div>
      </AuthenticatedLayout>
    );
  }

  if (subscription.status === "canceled") {
    return (
      <AuthenticatedLayout user={user} errors={errors} notice={notice}>
        <Head title="Billing" />
        <div className="flex max-w-lg mx-auto pt-4">
          <div className="w-full">
            <BillingCardShell>
              <div className="space-y-4">
                <p>Your subscription is canceled.</p>

                <div className="flex justify-end gap-2 pt-8">
                  <a href="/billing/portal" className={buttonVariants({ variant: "outline" })}>
                    Manage your subscription
                  </a>
                </div>
              </div>
            </BillingCardShell>

            <div className="max-w-md mx-auto pt-16">
              <PlanDetails buttonText="Resume your subscription" />
            </div>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (subscription.status === "active") {
    return (
      <AuthenticatedLayout user={user} errors={errors} notice={notice}>
        <Head title="Billing" />

        <div className="pt-4">
          <BackToDashboard />
        </div>

        <div className="flex max-w-lg mx-auto pt-4">
          <div className="w-full">
            <BillingCardShell>
              <div className="space-y-2">
                <p>Your subscription is active.</p>

                {subscription.cancel_at_period_end && subscription.current_period_ends_at && (
                  <p>
                    Your subscription will cancel automatically on{" "}
                    {formatDateFromISO(subscription.current_period_ends_at)}.
                  </p>
                )}

                <div className="flex justify-end gap-2 pt-8">
                  <a href="/billing/portal" className={buttonVariants({ variant: "outline" })}>
                    Manage your subscription
                  </a>
                </div>
              </div>
            </BillingCardShell>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (subscription.status === "trialing") {
    return (
      <AuthenticatedLayout user={user} errors={errors} notice={notice}>
        <Head title="Billing" />

        <div className="pt-4">
          <BackToDashboard />
        </div>

        <div className="flex max-w-lg mx-auto pt-4">
          <div className="w-full">
            <BillingCardShell>
              <div className="space-y-2">
                <p>Your subscription is active.</p>
                {subscription.trial_ends_at && (
                  <p>
                    Your free trial will be ending on{" "}
                    {formatDateFromISO(subscription.trial_ends_at)}.
                  </p>
                )}

                {subscription.cancel_at_period_end && subscription.current_period_ends_at && (
                  <p>
                    Your subscription will cancel automatically on{" "}
                    {formatDateFromISO(subscription.current_period_ends_at)}.
                  </p>
                )}

                <div className="flex justify-end gap-2 pt-8">
                  <a href="/billing/portal" className={buttonVariants({ variant: "outline" })}>
                    Manage your subscription
                  </a>
                </div>
              </div>
            </BillingCardShell>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (subscription.status === "incomplete") {
    return (
      <AuthenticatedLayout user={user} errors={errors} notice={notice}>
        <Head title="Billing" />

        <div className="pt-4">
          <BackToDashboard />
        </div>

        <div className="flex max-w-lg mx-auto pt-4">
          <div className="w-full">
            <BillingCardShell>
              <div className="space-y-4">
                <p>Your subscription is incomplete.</p>
                <p>
                  Please click the button below to fix your subscription in order to avoid any
                  disruption in service.
                </p>

                <div className="flex justify-end gap-2 pt-8">
                  <a href="/billing/portal" className={buttonVariants({ variant: "outline" })}>
                    Manage your subscription
                  </a>
                </div>
              </div>
            </BillingCardShell>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout user={user} errors={errors} notice={notice}>
      <Head title="Billing" />
      <div className="flex max-w-lg mx-auto pt-4">
        <div className="w-full">
          <BillingCardShell>
            <div className="space-y-4">
              <p>Your subscription is inactive.</p>

              <div className="flex justify-end gap-2 pt-8">
                <a href="/billing/portal" className={buttonVariants({ variant: "outline" })}>
                  Manage your subscription
                </a>
              </div>
            </div>
          </BillingCardShell>

          <div className="max-w-md mx-auto pt-16">
            <PlanDetails buttonText="Resume your subscription" />
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}
